import { normalize, Schema } from 'normalizr';
import {
  EntityState,
  FetchStatus,
  ItemFetchStatusResult,
  ItemsFetchStatus,
  NormalizedResult,
  Result,
  SavePayloadAction,
} from './index';

export const itemsFetchStatus = <S = FetchStatus>(
  data: string[],
  fetchStatus: S,
  existingFetchStatus: ItemsFetchStatus = {},
): ItemsFetchStatus => {
  return data.reduce((obj, key) => {
    const status =
      existingFetchStatus[key] !== FetchStatus.Loaded && fetchStatus;

    if (status) {
      obj[key] = status;
    }

    return obj;
  }, {});
};

export const filterItemsByFetchStatus = <S = FetchStatus>(
  data: string[],
  fetchStatus: S,
): ItemsFetchStatus =>
  data.reduce((obj, key) => {
    obj[key] = fetchStatus;

    return obj;
  }, {});

export const fetchStatusResult = (
  status: FetchStatus,
): ItemFetchStatusResult => ({
  isLoading: status === FetchStatus.Loading,
  isError: status === FetchStatus.Error,
  isPartial: status === FetchStatus.Partial,
  isSuccess: status === FetchStatus.Loaded,
  status,
});

export const dataNormalizer = <T, E, DataFromAPI>(
  data: DataFromAPI | DataFromAPI[],
  schema: Schema,
): NormalizedResult<E> => {
  const dataArr = Array.isArray(data) ? data : [data];

  return normalize<T, E, Result>(dataArr, [schema]);
};

export const saveNormalizedData = <
  State extends EntityState<any>,
  NormalizedEntityType extends Record<string, unknown>,
>(
  state: State,
  { payload }: SavePayloadAction<NormalizedEntityType>,
): State => {
  const { data, fetchStatus } = payload;

  const notFullyLoadedKeys = Object.keys(data).filter(
    (key) => state.itemsFetchStatus[key] !== FetchStatus.Loaded,
  );
  const statusResult = itemsFetchStatus(notFullyLoadedKeys, fetchStatus);

  return {
    ...state,
    items: {
      ...state.items,
      ...data,
    },
    itemsFetchStatus: {
      ...state.itemsFetchStatus,
      ...statusResult,
    },
  };
};
